'use client';

import {type ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {useI18n} from '@udemy-v2/i18n';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {
    Carousel,
    type CarouselPagerButtonProps,
    CarouselNextButton,
    CarouselPreviousButton,
    CarouselSlide,
    useCarouselSlideLayout4Cards,
} from '@udemy-v2/react-carousel-components';
import {Button} from '@udemy-v2/react-core-components';
import {Icon} from '@udemy-v2/icons';
import {ContentArea} from '@udemy-v2/react-layout-components';
import {
    CardAuthors,
    CardMediaImage,
    CardTitle,
    CourseProductCard,
    RatingTag,
    Tag,
} from '@udemy-v2/react-product-card-components';
import {Loader} from '@udemy-v2/react-reveal-components';

import {fetchInstructorCourses} from '@/features/instructor-profile/lib/profile-api';
import type {CourseOption} from '@/features/instructor-profile/lib/types';
import {formatCount} from '@/features/public-profile/lib/format-stats';

import styles from './all-courses.module.scss';

interface AllCoursesProps {
    instructorId?: number;
}

/**
 * A horizontally-scrollable carousel of the instructor's catalogue, fetched
 * client-side from `/taught-profile-courses/` and paginated as the learner
 * scrolls. Uses the carousel's 4-course-card slide layout (responsive 1→2→3→4
 * medium cards within a {@link ContentArea}).
 */
const MOBILE_COLLAPSED_COUNT = 4;
const MOBILE_EXPANDED_COUNT = 8;

export function AllCourses({instructorId}: AllCoursesProps) {
    const {gettext} = useI18n();
    // `null` on SSR + first client render; resolves to a boolean after mount.
    // Default to the desktop carousel until known.
    const isMobile = useMatchMedia('md-max') === true;

    const [courses, setCourses] = useState<CourseOption[]>([]);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    // Next 1-indexed page to fetch; kept in a ref so onLoadMore stays stable.
    const nextPageRef = useRef(1);
    const isFetchingRef = useRef(false);

    const fetchNextPage = useCallback(async () => {
        if (instructorId == null || isFetchingRef.current) {
            return;
        }
        isFetchingRef.current = true;
        try {
            const page = nextPageRef.current;
            const {results, count} = await fetchInstructorCourses(instructorId, page);
            setTotalCount(count);
            setCourses((prev) => [...prev, ...results]);
            nextPageRef.current = page + 1;
        } catch {
            setHasError(true);
        } finally {
            isFetchingRef.current = false;
        }
    }, [instructorId]);

    // Load the first batch once the instructor id is available.
    useEffect(() => {
        if (instructorId == null) {
            return;
        }
        let active = true;
        setIsInitialLoading(true);
        fetchNextPage().finally(() => {
            if (active) {
                setIsInitialLoading(false);
            }
        });
        return () => {
            active = false;
        };
    }, [instructorId, fetchNextPage]);

    if (instructorId == null) {
        return null;
    }

    // After the first batch resolves with nothing, render nothing at all.
    if (!isInitialLoading && courses.length === 0) {
        return null;
    }

    const hasMore = totalCount != null && courses.length < totalCount;

    return (
        <section className={styles['all-courses']} aria-labelledby="all-courses-heading">
            <ContentArea>
                {isInitialLoading ? (
                    <>
                        <div className={styles['header-row']}>
                            <h2 id="all-courses-heading" className={styles['heading']}>
                                {gettext('All courses')}
                            </h2>
                        </div>
                        <div className={styles['loader']}>
                            <Loader size="large" label={gettext('Loading courses')} />
                        </div>
                    </>
                ) : isMobile ? (
                    <MobileCourseList
                        courses={courses}
                        hasMore={hasMore}
                        hasError={hasError}
                    />
                ) : (
                    <CoursesCarousel
                        courses={courses}
                        onLoadMore={hasMore ? fetchNextPage : undefined}
                        hasError={hasError}
                    />
                )}
            </ContentArea>
        </section>
    );
}

interface CoursesCarouselProps {
    courses: CourseOption[];
    onLoadMore?: () => Promise<void>;
    hasError: boolean;
}


function CoursesCarousel({courses, onLoadMore, hasError}: CoursesCarouselProps) {
    const {gettext, ninterpolate, locale} = useI18n();
    const slideLayout = useCarouselSlideLayout4Cards();

    // Portal targets for the pager buttons. State (not refs) so that the first
    // post-mount render re-invokes the render props once the slots exist.
    const [prevSlot, setPrevSlot] = useState<HTMLElement | null>(null);
    const [nextSlot, setNextSlot] = useState<HTMLElement | null>(null);

    const slides = courses.map((course) => {
        const authors = course.visible_instructors
            .map((instructor) => instructor.display_name ?? instructor.name)
            .join(', ');

        const tags = buildCourseTags(course, ninterpolate, locale);

        return (
            <CarouselSlide
                key={course.id}
                aria-label={course.title}
                className={styles['carousel-slide']}
            >
                <CourseProductCard
                    className={styles['course-card']}
                    layout="vertical"
                    mediaWide={
                        <CardMediaImage
                            layout="wide"
                            src={course.image_480x270 ?? course.image_750x422}
                            src2x={course.image_750x422}
                            alt=""
                        />
                    }
                    mediaSquare={
                        <CardMediaImage layout="square" src={course.image_100x100} alt="" />
                    }
                    title={
                        <CardTitle renderLink={CardTitle.renderAnchorTag({href: course.url})}>
                            {course.title}
                        </CardTitle>
                    }
                    authors={
                        authors ? (
                            <CardAuthors label={gettext('Instructors: ')} authors={authors} />
                        ) : undefined
                    }
                    tags={tags}
                />
            </CarouselSlide>
        );
    });

    const renderPreviousButton = (props: CarouselPagerButtonProps) =>
        prevSlot
            ? createPortal(
                  <CarouselPreviousButton
                      {...props}
                      size="small"
                      udStyle="secondary"
                      hideWhenDisabled={false}
                  />,
                  prevSlot,
              )
            : null;

    const renderNextButton = (props: CarouselPagerButtonProps) =>
        nextSlot
            ? createPortal(
                  <CarouselNextButton
                      {...props}
                      size="small"
                      udStyle="secondary"
                      hideWhenDisabled={false}
                  />,
                  nextSlot,
              )
            : null;

    return (
        <>
            <div className={styles['header-row']}>
                <h2 id="all-courses-heading" className={styles['heading']}>
                    {gettext('All courses')}
                </h2>
                <div className={styles['pager']}>
                    <span ref={setPrevSlot} className={styles['pager-slot']} />
                    <span ref={setNextSlot} className={styles['pager-slot']} />
                </div>
            </div>
            <div className={styles['all-courses-carousel']}>
                <Carousel
                    ariaLabel={gettext('All courses')}
                    slideLayout={slideLayout}
                    slides={slides}
                    onLoadMore={onLoadMore}
                    onLoadMorePageThreshold={1}
                    renderPreviousButton={renderPreviousButton}
                    renderNextButton={renderNextButton}
                />
            </div>
            {hasError && (
                <p role="alert" className={styles['error']}>
                    {gettext('Some courses could not be loaded. Please try again later.')}
                </p>
            )}
        </>
    );
}

/**
 * Builds the metadata tag list shared by the desktop and mobile cards:
 * a star rating followed by the formatted review count.
 */
function buildCourseTags(
    course: CourseOption,
    ninterpolate: ReturnType<typeof useI18n>['ninterpolate'],
    locale: string,
): ReactNode[] {
    const tags: ReactNode[] = [];
    if (course.rating) {
        tags.push(<RatingTag key="rating" rating={course.rating} />);
    }
    if (course.num_reviews) {
        tags.push(
            <Tag key="reviews">
                {ninterpolate('%(count)s rating', '%(count)s ratings', course.num_reviews, {
                    count: formatCount(course.num_reviews, locale),
                })}
            </Tag>,
        );
    }
    return tags;
}

interface MobileCourseListProps {
    courses: CourseOption[];
    hasMore: boolean;
    hasError: boolean;
}

/**
 * Mobile variant of "All courses" — no carousel. Shows the first
 * {@link MOBILE_COLLAPSED_COUNT} cards collapsed, expanding to
 * {@link MOBILE_EXPANDED_COUNT} via an animated drawer (500 ms).
 */
function MobileCourseList({courses, hasMore, hasError}: MobileCourseListProps) {
    const {gettext, ninterpolate, locale} = useI18n();
    const [isExpanded, setIsExpanded] = useState(false);

    const collapsedCourses = courses.slice(0, MOBILE_COLLAPSED_COUNT);
    const expandedCourses = courses.slice(MOBILE_COLLAPSED_COUNT, MOBILE_EXPANDED_COUNT);
    // Show the toggle when there are more courses beyond the collapsed set.
    const hasToggle = courses.length > MOBILE_COLLAPSED_COUNT || hasMore;

    const renderCard = (course: CourseOption) => {
        const authors = course.visible_instructors
            .map((instructor) => instructor.display_name ?? instructor.name)
            .join(', ');

        return (
            <li key={course.id} className={styles['mobile-item']}>
                <CourseProductCard
                    layout="condensed"
                    mediaSquare={
                        <CardMediaImage layout="square" src={course.image_100x100} alt="" />
                    }
                    mediaWide={
                        <CardMediaImage
                            layout="wide"
                            src={course.image_480x270 ?? course.image_750x422}
                            src2x={course.image_750x422}
                            alt=""
                        />
                    }
                    title={
                        <CardTitle
                            headingLevel="h3"
                            renderLink={CardTitle.renderAnchorTag({href: course.url})}
                        >
                            {course.title}
                        </CardTitle>
                    }
                    authors={
                        authors ? (
                            <CardAuthors label={gettext('Instructors: ')} authors={authors} />
                        ) : undefined
                    }
                    tags={buildCourseTags(course, ninterpolate, locale)}
                />
            </li>
        );
    };

    return (
        <>
            <div className={styles['header-row']}>
                <h2 id="all-courses-heading" className={styles['heading']}>
                    {gettext('All courses')}
                </h2>
            </div>
            <ul className={styles['mobile-list']}>
                {collapsedCourses.map(renderCard)}
                {expandedCourses.length > 0 && (
                    <li
                        className={`${styles['drawer-item']} ${isExpanded ? styles['drawer-item--expanded'] : ''}`}
                        aria-hidden={!isExpanded}
                    >
                        <ul className={styles['drawer-inner']}>
                            {expandedCourses.map(renderCard)}
                        </ul>
                    </li>
                )}
                {hasToggle && (
                    <li className={styles['show-more-item']}>
                        <Button
                            udStyle="ghost"
                            size="xsmall"
                            className={styles['show-more-button']}
                            onClick={() => setIsExpanded((prev) => !prev)}
                            aria-expanded={isExpanded}
                        >
                            {isExpanded ? gettext('Show less') : gettext('Show more')}
                            <Icon glyph={isExpanded ? 'collapse' : 'expand'} label={false} />
                        </Button>
                    </li>
                )}
            </ul>
            {hasError && (
                <p role="alert" className={styles['error']}>
                    {gettext('Some courses could not be loaded. Please try again later.')}
                </p>
            )}
        </>
    );
}
